import { Button, Flex, Typography } from 'antd'
import { isEmpty } from 'lodash'
import React, { useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomDraggableInput from 'src/app/shared/components/common/draggable-input'
import CustomSelect from 'src/app/shared/components/common/select'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { validateOnBlur } from 'src/app/shared/utils/String.util'
import { colorTextGray1, colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import { FILE_ACCEPT, LIMITED_FILE_SIZE_IN_MB, useFormRules } from '../index.constant'
import { FormValues, useSubmitIssuedCheckFile } from '../index.context'

const SubmitIssuedCheckForm: React.FC = () => {
  const {
    form,
    clients,
    accounts,
    fileMappings,
    loading,
    validatedFromBE,
    loadingClients,
    beforeUpload,
    onSubmit,
    setValidatedFromBE,
  } = useSubmitIssuedCheckFile()
  const { control, formState, handleSubmit } = useMemo(() => form, [form])
  const { errors, isValid, isDirty } = useMemo(() => formState, [formState])
  const { t } = useTranslation()
  const rules = useFormRules()
  const { userPermissions } = useRolePermission()
  const { mode } = useWindowSize()

  const canSubmit = useMemo(
    () => userPermissions[ESubModules.SUBMIT_ISSUE_CHEK_FIL]?.includes(EPermissions.UPLOAD),
    [userPermissions],
  )

  const canView = useMemo(
    () => userPermissions[ESubModules.SUBMIT_ISSUE_CHEK_FIL]?.includes(EPermissions.VIEW_ITEMS),
    [userPermissions],
  )
  const canAddEdit = useMemo(
    () => userPermissions[ESubModules.SUBMIT_ISSUE_CHEK_FIL]?.includes(EPermissions.ADD_EDIT_ITEMS),
    [userPermissions],
  )

  const isReadOnly = useMemo(() => {
    if (canAddEdit) {
      return false
    } else if (canView) {
      return true
    }
  }, [canAddEdit, canView])

  const isAvaiToSubmit = useMemo(() => {
    if (canSubmit) {
      return true
    } else {
      return false
    }
  }, [canSubmit])

  const fileMappingId = useWatch({
    control: form.control,
    name: 'mappingFormat',
  })

  const showAllAccount = useMemo(
    () => fileMappings.find((item) => item.fileMappingFormatId === fileMappingId)?.showAllAccount,
    [fileMappingId, fileMappings],
  )

  const accountOptions = useMemo(() => {
    if (accounts.length > 0) {
      const options = [
        ...accounts.map((item) => ({
          label: item.accountName,
          value: item.accountId,
        })),
      ]
      if (showAllAccount) {
        options.unshift({
          label: t('AllTransactions:title.all_accounts'),
          value: null as any,
        })
      }

      return options
    }
    return []
  }, [accounts, showAllAccount, t])

  return (
    <div
      style={{
        borderColor: colorTextGray1,
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 16,
        padding: 16,
        boxShadow: '0px 2px 4px #EAEBEC',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          gap={24}
          style={{
            flexDirection: 'column',
          }}
        >
          <Controller
            control={control}
            name='clientId'
            render={({ field, fieldState }) => (
              <CustomSelect
                showSearch={true}
                label={t('SubmitIssuedCheck:title.client_name')}
                placeholder={t('SubmitIssuedCheck:placeholder.client_name')}
                value={field.value}
                onChange={field.onChange}
                onBlur={() => validateOnBlur<FormValues>('clientId', field, form)}
                error={fieldState.error}
                options={
                  clients?.map((item) => ({
                    label: item.clientName,
                    value: item.id,
                  })) ?? []
                }
                loading={loadingClients}
                disabled={isReadOnly}
                required={(rules.clientId.required as any).value}
              />
            )}
            rules={rules.clientId}
          />
          <Controller
            control={control}
            name='file'
            render={({ field, fieldState }) => (
              <div>
                <CustomDraggableInput
                  label={t('SubmitIssuedCheck:title.select_file')}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                  disabled={!isAvaiToSubmit}
                  required={(rules.file.required as any).value}
                  style={{
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                  hideUploadBtn={!isAvaiToSubmit}
                  beforeUpload={beforeUpload}
                  accept={Object.values(FILE_ACCEPT).join(', ')}
                  uploadBtnText={t('SubmitIssuedCheck:title.select')}
                />

                <Flex
                  style={{
                    justifyContent: 'space-between',
                    marginTop: 8,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontFamily: 'MyriadPro',
                      fontWeight: 400,
                      fontSize: 14,
                      color: colorTextSubtle,
                    }}
                  >
                    {t('SubmitIssuedCheck:help_text.file_support')}
                  </Typography.Text>
                  <Typography.Text
                    style={{
                      fontFamily: 'MyriadPro',
                      fontWeight: 400,
                      fontSize: 14,
                      color: colorTextSubtle,
                    }}
                  >
                    {t('SubmitIssuedCheck:help_text.max_size', {
                      maxSize: LIMITED_FILE_SIZE_IN_MB,
                    })}
                  </Typography.Text>
                </Flex>
              </div>
            )}
            rules={rules.file}
          />
          <Flex
            style={{
              flexDirection: mode === 'mobile' ? 'column' : undefined,
            }}
            gap={24}
          >
            <Controller
              control={control}
              name='mappingFormat'
              render={({ field, fieldState }) => (
                <CustomSelect
                  label={t('SubmitIssuedCheck:title.mapping_format')}
                  placeholder={t('SubmitIssuedCheck:placeholder.mapping_format')}
                  value={field.value}
                  onChange={(value, option) => {
                    if (validatedFromBE) {
                      form.clearErrors('file')
                      setValidatedFromBE(false)
                    }
                    field.onChange(value, option)
                  }}
                  onBlur={() => validateOnBlur<FormValues>('mappingFormat', field, form)}
                  error={fieldState.error}
                  options={fileMappings.map((item) => ({
                    value: item.fileMappingFormatId,
                    label: item.fileName,
                  }))}
                  loading={loadingClients}
                  required={(rules.mappingFormat.required as any).value}
                  showSearch
                  disabled={isReadOnly}
                />
              )}
              rules={rules.mappingFormat}
            />
            <Controller
              control={control}
              name='accountId'
              disabled={isReadOnly || !fileMappingId}
              render={({ field, fieldState }) => (
                <CustomSelect
                  showSearch={true}
                  label={t('SubmitIssuedCheck:title.account_name')}
                  placeholder={t('SubmitIssuedCheck:placeholder.account_name')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => validateOnBlur<FormValues>('accountId', field, form)}
                  error={fieldState.error}
                  options={accountOptions}
                  loading={loadingClients}
                  // required={(rules.accountId.required as any).value}
                  disabled={isReadOnly || !fileMappingId}
                  sortWithAll
                />
              )}
              rules={rules.accountId}
            />
          </Flex>
        </Flex>
        <Flex
          style={{
            justifyContent: 'flex-end',
            marginTop: 24,
          }}
        >
          <Button
            type='primary'
            className='add-check-btn'
            disabled={!isValid || !isEmpty(errors) || !isDirty || loading}
            htmlType='button'
            onClick={handleSubmit(onSubmit)}
            loading={loading}
          >
            {t('SubmitIssuedCheck:title.process_file')}
          </Button>
        </Flex>
      </form>
    </div>
  )
}

export default SubmitIssuedCheckForm
